import React, { useEffect, useRef } from 'react';
import { Mic, MicOff, MonitorUp, Video, VideoOff } from 'lucide-react';

const attachTrack = (track, element) => {
  if (!track || !element) return undefined;
  track.attach(element);
  return () => {
    track.detach(element);
  };
};

const tileVariantClassName = {
  featured: 'aspect-video min-h-[320px] lg:min-h-[540px]',
  default: 'aspect-[4/3] min-h-[180px] md:aspect-video md:min-h-[200px] lg:min-h-[220px]',
  compact: 'aspect-video min-h-[110px] sm:min-h-[130px]',
};

const ParticipantTile = ({
  participant,
  variant = 'default',
  interactive = false,
  isFocused = false,
  onDoubleClick,
}) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const activeVideoTrack = participant.videoTrack;
  const isScreenTile = participant.tileType === 'screen';
  const resolvedVariant = isScreenTile && variant === 'default' ? 'featured' : variant;
  const tileSizeClassName = tileVariantClassName[resolvedVariant] || tileVariantClassName.default;
  const compact = resolvedVariant === 'compact';

  useEffect(() => attachTrack(activeVideoTrack, videoRef.current), [activeVideoTrack]);
  useEffect(() => {
    if (participant.isLocal) return undefined;
    return attachTrack(participant.audioTrack, audioRef.current);
  }, [participant.audioTrack, participant.isLocal]);

  return (
    <article
      onDoubleClick={onDoubleClick}
      className={`relative overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-950 shadow-[0_18px_50px_rgba(15,23,42,0.16)] ${
        participant.isSpeaking ? 'ring-2 ring-emerald-400/80 ring-offset-2 ring-offset-slate-950' : ''
      } ${
        isFocused ? 'ring-2 ring-sky-300/80 ring-offset-2 ring-offset-slate-950' : ''
      } ${
        interactive ? 'cursor-pointer select-none' : ''
      }`}
    >
      <div className={`relative ${tileSizeClassName} bg-[radial-gradient(circle_at_top,#184b4b_0%,#020617_70%)]`}>
        {activeVideoTrack ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={participant.isLocal}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full" aria-hidden="true" />
        )}

        {!participant.isLocal && participant.audioTrack ? (
          <audio ref={audioRef} autoPlay playsInline className="hidden" />
        ) : null}

        <div className={`absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent ${compact ? 'p-2.5' : 'p-3 md:p-4'}`}>
          <div className="min-w-0">
            <div className={`inline-flex items-center gap-2 rounded-full bg-white/12 font-semibold text-white backdrop-blur-sm ${compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1 text-xs'}`}>
              <span className="truncate">{participant.name}</span>
              {participant.isLocal ? <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]">You</span> : null}
            </div>
            {isScreenTile ? (
              <p className={`font-medium text-white/80 ${compact ? 'mt-1 text-[11px]' : 'mt-2 text-xs'}`}>Screen sharing</p>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center justify-center rounded-full border ${
              compact ? 'h-7 w-7' : 'h-8 w-8 md:h-9 md:w-9'
            } ${
              participant.microphoneEnabled
                ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200'
                : 'border-rose-400/30 bg-rose-500/15 text-rose-200'
            }`}>
              {participant.microphoneEnabled ? <Mic className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} /> : <MicOff className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />}
            </span>
            <span className={`inline-flex items-center justify-center rounded-full border ${
              compact ? 'h-7 w-7' : 'h-8 w-8 md:h-9 md:w-9'
            } ${
              participant.cameraEnabled || participant.screenShareEnabled
                ? 'border-sky-400/40 bg-sky-500/15 text-sky-200'
                : 'border-slate-500/40 bg-slate-500/15 text-slate-200'
            }`}>
              {isScreenTile ? <MonitorUp className="h-4 w-4" /> : (
                participant.cameraEnabled
                  ? <Video className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                  : <VideoOff className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              )}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ParticipantTile;
